import React, { useCallback } from 'react'

import { Button } from '@cgtrader/triangle-ui/dist/src/ssr-index'
import {
  bool, func, number, string,
} from 'prop-types'

import I18n from 'Pods/Shared/I18n'

import formatPrice from 'Utils/formatPrice'
import { EMPTY_ARRAY } from 'Utils/stableDefaults'

import { PLAN_TYPE, TIER } from '../../constants'
import { planChangesShape } from '../../propTypes'

const KEY = 'subscription_management.cancel_modal.save_offers'
const COLLECTIONS_URL = '/3d-collections'
const MONTHS_PER_YEAR = 12

const OFFER = { DOWNGRADE: 'downgrade', MORE: 'more', REFRAME: 'reframe' }

const OFFERS_BY_REASON = {
  not_enough_models: [OFFER.MORE],
  not_worth_price: [OFFER.DOWNGRADE, OFFER.REFRAME],
  not_used_enough: [OFFER.DOWNGRADE],
  prefer_individual: [OFFER.REFRAME, OFFER.DOWNGRADE],
}

const availableTarget = (planChanges, target) => (
  planChanges?.targets.find((option) => option.target === target && option.available) || null
)

const formatWholePrice = (price) => `$${price.toLocaleString('en-US')}`

function MoreRow({
  title, description, cta, href,
}) {
  return (
    <li className="save-offer__row">
      <div className="save-offer__row-text">
        <span className="cgt-body-base-semibold">{title}</span>
        <span className="cgt-caption save-offer__row-description">{description}</span>
      </div>
      <Button type="secondary" size="small" href={href} customClass="save-offer__row-action">
        {cta}
      </Button>
    </li>
  )
}

MoreRow.propTypes = {
  title: string.isRequired,
  description: string.isRequired,
  cta: string.isRequired,
  href: string.isRequired,
}

function AltRow({
  text, cta, onClick, isLoading = false,
}) {
  return (
    <li className="save-offers__alt">
      <span className="cgt-body-base save-offers__alt-text">{text}</span>
      <Button
        type="secondary-with-primary-border"
        size="small"
        actionType="button"
        customClass="save-offers__alt-action"
        onClick={onClick}
        disabled={isLoading}
        loading={isLoading}
      >
        {cta}
      </Button>
    </li>
  )
}

AltRow.propTypes = {
  text: string.isRequired,
  cta: string.isRequired,
  onClick: func.isRequired,
  isLoading: bool,
}

function ReframeCard({
  count, value, monthlyCost, onKeep, translate,
}) {
  return (
    <div className="save-offer">
      <h4 className="cgt-body-md-semibold save-offer__title">{translate(`${KEY}.reframe.title`)}</h4>
      <p className="cgt-body-base save-offer__body">
        {translate(`${KEY}.reframe.body`, { count, value, monthlyCost })}
      </p>
      <div className="save-offer__comparison">
        <div className="save-offer__comparison-item">
          <span className="cgt-heading-300 save-offer__comparison-value">{value}</span>
          <span className="cgt-caption save-offer__comparison-label">
            {translate(`${KEY}.reframe.individual_label`)}
          </span>
        </div>
        <span className="cgt-body-base-semibold save-offer__comparison-vs">{translate(`${KEY}.reframe.vs`)}</span>
        <div className="save-offer__comparison-item">
          <span className="cgt-heading-300 save-offer__comparison-value save-offer__comparison-value--plan">
            {monthlyCost}
          </span>
          <span className="cgt-caption save-offer__comparison-label">{translate(`${KEY}.reframe.plan_label`)}</span>
        </div>
      </div>
      <Button type="primary" size="small" actionType="button" customClass="save-offer__action" onClick={onKeep}>
        {translate(`${KEY}.reframe.action`)}
      </Button>
    </div>
  )
}

ReframeCard.propTypes = {
  count: number.isRequired,
  value: string.isRequired,
  monthlyCost: string.isRequired,
  onKeep: func.isRequired,
  translate: func.isRequired,
}

function SaveOffer({
  reason = null,
  tier,
  billingTerm = PLAN_TYPE.MONTHLY,
  planChanges = null,
  essentialsPrice = null,
  currentPrice = null,
  premiumModelCount = null,
  premiumGenerations = null,
  effectiveDate = null,
  downloadedCount = null,
  downloadedValue = null,
  upgradeUrl,
  isScheduling = false,
  onDowngrade,
  onKeep,
  locale = 'en',
}) {
  const translate = useCallback(
    (key, options = {}) => I18n.t(key, { ns: 'rails', lng: locale, ...options }),
    [locale]
  )
  const isAnnual = billingTerm === PLAN_TYPE.ANNUAL
  const priceLabel = (price) => translate(
    `subscription_management.active.price_per_${isAnnual ? 'year' : 'month'}`,
    { price: formatPrice(price) }
  )

  const downgradeTarget = tier === TIER.PREMIUM && availableTarget(planChanges, `${TIER.ESSENTIALS}_${billingTerm}`)
  const upgradeTarget = tier === TIER.ESSENTIALS && premiumModelCount && premiumGenerations
    && availableTarget(planChanges, `${TIER.PREMIUM}_${billingTerm}`)

  const monthlyCost = currentPrice && (isAnnual ? currentPrice / MONTHS_PER_YEAR : currentPrice)
  const roundedValue = downloadedValue === null ? null : Math.round(downloadedValue)
  const isOfferAvailable = {
    [OFFER.DOWNGRADE]: Boolean(downgradeTarget && essentialsPrice && currentPrice && effectiveDate),
    [OFFER.MORE]: true,
    // Both values must exceed the cost: rounding up turns $34.99 into $35, rounding down reads "$29 vs $29.17".
    [OFFER.REFRAME]: downloadedCount !== null && roundedValue !== null && monthlyCost > 0
      && downloadedValue > monthlyCost && roundedValue > monthlyCost,
  }
  const [primary, ...alternatives] = (OFFERS_BY_REASON[reason] || EMPTY_ARRAY)
    .filter((offer) => isOfferAvailable[offer])

  if (!primary) return null

  const reframeValue = roundedValue === null ? null : formatWholePrice(roundedValue)
  const handleDowngrade = () => onDowngrade(downgradeTarget.target)

  let card
  if (primary === OFFER.DOWNGRADE) {
    card = (
      <div className="save-offer">
        <h4 className="cgt-body-md-semibold save-offer__title">{translate(`${KEY}.downgrade.title`)}</h4>
        <p className="cgt-body-base save-offer__body">
          {translate(`${KEY}.downgrade.body_${isAnnual ? 'year' : 'month'}`, {
            essentialsPrice: priceLabel(essentialsPrice),
            currentPrice: priceLabel(currentPrice),
            date: effectiveDate,
          })}
        </p>
        <Button
          type="primary"
          size="small"
          actionType="button"
          customClass="save-offer__action"
          onClick={handleDowngrade}
          disabled={isScheduling}
          loading={isScheduling}
        >
          {translate(`${KEY}.downgrade.action`)}
        </Button>
      </div>
    )
  } else if (primary === OFFER.REFRAME) {
    card = (
      <ReframeCard
        count={downloadedCount}
        value={reframeValue}
        monthlyCost={formatPrice(monthlyCost)}
        onKeep={onKeep}
        translate={translate}
      />
    )
  } else {
    card = (
      <div className="save-offer">
        <h4 className="cgt-body-md-semibold save-offer__title">{translate(`${KEY}.more.title`)}</h4>
        <p className="cgt-body-base save-offer__body">{translate(`${KEY}.more.body`)}</p>
        <ul className="save-offer__rows">
          {upgradeTarget && (
            <MoreRow
              title={translate(`${KEY}.more.upgrade.title`)}
              description={translate(`${KEY}.more.upgrade.description`, {
                modelCount: premiumModelCount, generations: premiumGenerations,
              })}
              cta={translate(`${KEY}.more.upgrade.action`)}
              href={`${upgradeUrl}?target=${upgradeTarget.target}`}
            />
          )}
          <MoreRow
            title={translate(`${KEY}.more.collections.title`)}
            description={translate(`${KEY}.more.collections.description`)}
            cta={translate(`${KEY}.more.collections.action`)}
            href={COLLECTIONS_URL}
          />
        </ul>
      </div>
    )
  }

  return (
    <div className="save-offers">
      {card}
      {alternatives.length > 0 && (
        <>
          <h5 className="cgt-caption-semibold save-offers__alt-heading">{translate(`${KEY}.another_option`)}</h5>
          <ul className="save-offers__alts">
            {alternatives.map((offer) => (offer === OFFER.DOWNGRADE ? (
              <AltRow
                key={offer}
                text={translate(`${KEY}.downgrade.alt_text`, { essentialsPrice: priceLabel(essentialsPrice) })}
                cta={translate(`${KEY}.downgrade.action`)}
                onClick={handleDowngrade}
                isLoading={isScheduling}
              />
            ) : (
              <AltRow
                key={offer}
                text={translate(`${KEY}.reframe.alt_text`, { count: downloadedCount, value: reframeValue })}
                cta={translate(`${KEY}.reframe.alt_action`)}
                onClick={onKeep}
              />
            )))}
          </ul>
        </>
      )}
    </div>
  )
}

SaveOffer.propTypes = {
  reason: string,
  tier: string.isRequired,
  billingTerm: string,
  planChanges: planChangesShape,
  essentialsPrice: number,
  currentPrice: number,
  premiumModelCount: string,
  premiumGenerations: number,
  effectiveDate: string,
  downloadedCount: number,
  downloadedValue: number,
  upgradeUrl: string.isRequired,
  isScheduling: bool,
  onDowngrade: func.isRequired,
  onKeep: func.isRequired,
  locale: string,
}

export default SaveOffer
